<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h3>Create Custom Field</h3>
        <button @click="$emit('close')" class="btn-close">×</button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <!-- 1. Type Field -->
          <div class="form-group">
            <label for="object-name">Type <span class="required">*</span></label>
            <select
              id="object-name"
              v-model="formData.object_name"
              required
              class="form-control"
            >
              <option value="">Select Type</option>
              <option value="Job">Job</option>
              <option value="Company">Company</option>
              <option value="Both">Both</option>
            </select>
          </div>

          <!-- 2. Name Field -->
          <div class="form-group">
            <label for="display-name">Name <span class="required">*</span></label>
            <input
              id="display-name"
              v-model="displayName"
              type="text"
              required
              class="form-control"
              placeholder="Enter field name (e.g., Field Name)"
              @input="updateFieldName"
            />
            <div class="field-hint">
              <span>Display Name: <strong>{{ displayName || '—' }}</strong></span>
              <span>Field Name: <code>{{ fieldName || '—' }}</code></span>
            </div>
          </div>

          <!-- 3. Field Type -->
          <div class="form-group">
            <label for="field-type">Field Type <span class="required">*</span></label>
            <select
              id="field-type"
              v-model="formData.field_type"
              required
              class="form-control"
            >
              <option value="">Select Field Type</option>
              <option value="Text">Text</option>
              <option value="Numeric">Numeric</option>
              <option value="Currency">Currency</option>
              <option value="Date">Date</option>
              <option value="Datetime">Datetime</option>
              <option value="SelectList">SelectList</option>
            </select>
          </div>

          <!-- 4. Select List Items (Conditional) -->
          <div v-if="formData.field_type === 'SelectList'" class="form-group">
            <label>List Options</label>
            <div class="select-list-container">
              <div
                v-for="(item, index) in selectListItems"
                :key="index"
                class="select-list-item"
              >
                <input
                  v-model="selectListItems[index]"
                  type="text"
                  class="form-control"
                  :placeholder="`Option ${index + 1}`"
                />
                <button
                  v-if="selectListItems.length > 1"
                  @click.prevent="removeSelectListItem(index)"
                  type="button"
                  class="btn-remove-item"
                >
                  ×
                </button>
              </div>
              <button
                @click.prevent="addSelectListItem"
                type="button"
                class="btn-add-item"
              >
                <span>+</span> Add Option
              </button>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="error-message">{{ error }}</div>

          <!-- Submit Button -->
          <div class="modal-actions">
            <button type="button" @click="$emit('close')" class="btn-cancel">
              Cancel
            </button>
            <button type="submit" class="btn-submit" :disabled="submitting">
              {{ submitting ? 'Creating...' : 'Create Field' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CreateCustomFieldModal',
  data() {
    return {
      displayName: '',
      fieldName: '',
      formData: {
        object_name: '',
        field_type: '',
      },
      selectListItems: [''],
      submitting: false,
      error: null,
    }
  },
  methods: {
    updateFieldName() {
      // Generate field_name from display_name
      // Lowercase, replace spaces with underscores, only letters and numbers
      this.fieldName = this.displayName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '') // Remove non-alphanumeric except spaces
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, '') // Remove leading/trailing underscores
    },
    addSelectListItem() {
      this.selectListItems.push('')
    },
    removeSelectListItem(index) {
      this.selectListItems.splice(index, 1)
    },
    async handleSubmit() {
      this.error = null

      // Validation
      if (!this.formData.object_name) {
        this.error = 'Type is required'
        return
      }
      if (!this.displayName || !this.displayName.trim()) {
        this.error = 'Name is required'
        return
      }
      // Validate that field_name was generated correctly
      if (!this.fieldName || !this.fieldName.trim()) {
        this.error = 'Name must contain at least one letter or number'
        return
      }
      if (!this.formData.field_type) {
        this.error = 'Field Type is required'
        return
      }
      if (this.formData.field_type === 'SelectList') {
        // Filter out empty items
        const validItems = this.selectListItems.filter(item => item && item.trim())
        if (validItems.length === 0) {
          this.error = 'At least one list option is required for SelectList fields'
          return
        }
      }

      this.submitting = true

      try {
        const ssoToken = await this.getSSOToken()
        const headers = {
          'x-sso-token': ssoToken,
          'Content-Type': 'application/json',
        }

        // Prepare the field data
        const fieldData = {
          field_type: this.formData.field_type,
          field_name: this.fieldName,
          display_name: this.displayName.trim(),
        }

        // Handle SelectList items (filter empty ones)
        if (this.formData.field_type === 'SelectList') {
          fieldData.select_list_items = this.selectListItems
            .filter(item => item && item.trim())
            .map(item => item.trim())
        }

        // Handle "Both" type - create two separate fields
        if (this.formData.object_name === 'Both') {
          // Create Job field
          const jobData = {
            ...fieldData,
            object_name: 'Job',
          }
          await fetch('/servicem8/custom-fields', {
            method: 'POST',
            headers,
            body: JSON.stringify(jobData),
          })

          // Create Company field
          const companyData = {
            ...fieldData,
            object_name: 'Company',
          }
          const response = await fetch('/servicem8/custom-fields', {
            method: 'POST',
            headers,
            body: JSON.stringify(companyData),
          })

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
            throw new Error(errorData.error || 'Failed to create custom fields')
          }
        } else {
          // Single field
          fieldData.object_name = this.formData.object_name

          const response = await fetch('/servicem8/custom-fields', {
            method: 'POST',
            headers,
            body: JSON.stringify(fieldData),
          })

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
            throw new Error(errorData.error || 'Failed to create custom field')
          }
        }

        // Success - emit event to parent
        this.$emit('created')
      } catch (error) {
        console.error('Error creating custom field:', error)
        this.error = error.message || 'Failed to create custom field'
      } finally {
        this.submitting = false
      }
    },
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.btn-close {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-close:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.required {
  color: #d32f2f;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #24ACFE;
  box-shadow: 0 0 0 3px rgba(36, 172, 254, 0.1);
}

.field-hint {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-hint code {
  font-family: 'Courier New', monospace;
  color: #d63384;
  background: white;
  padding: 2px 4px;
  border-radius: 2px;
}

.select-list-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.select-list-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.select-list-item .form-control {
  flex: 1;
}

.btn-remove-item {
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  color: #666;
  border-radius: 4px;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.btn-remove-item:hover {
  background: #e0e0e0;
  color: #333;
}

.btn-add-item {
  padding: 8px 12px;
  border: 1px dashed #ddd;
  background: white;
  color: #666;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn-add-item:hover {
  border-color: #24ACFE;
  color: #24ACFE;
  background: #f0f8ff;
}

.btn-add-item span {
  font-size: 18px;
  font-weight: bold;
}

.error-message {
  padding: 12px;
  background: #ffebee;
  color: #d32f2f;
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 20px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.btn-cancel,
.btn-submit {
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  background: #f5f5f5;
  color: #333;
  border: none;
}

.btn-cancel:hover {
  background: #e0e0e0;
}

.btn-submit {
  background: #24ACFE;
  color: white;
  border: none;
}

.btn-submit:hover:not(:disabled) {
  background: #1a8fd8;
}

.btn-submit:disabled {
  background: #a0d9ff;
  cursor: not-allowed;
}
</style>
